import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

// Parent-portal messages API. Mirrors server shapes from
// [server/src/routes/portal_messages.js]. Dual-audience: parents see only
// their linked client's messages; staff with `view_portal` see every message
// in their client scope (list endpoint groups by clientId). Writes use a
// direction flag — `practice_to_parent` for staff, `parent_to_practice` for
// parent accounts.

export interface PortalMessage {
    id: string;
    from: 'staff' | 'parent';
    sender: string;
    text: string;
    /** Pre-formatted display time from the server (e.g. "4/22 3:14pm"). */
    time: string;
    read: boolean;
}

export type PortalMessageMap = Record<string, PortalMessage[]>;

async function fetchMessages(): Promise<PortalMessageMap> {
    const res = await api.get<PortalMessageMap>('/portal-messages');
    return res.data;
}

export function usePortalMessages() {
    return useQuery({
        queryKey: ['portal-messages'] as const,
        queryFn: fetchMessages,
        staleTime: 10_000,
    });
}

export interface SendPortalMessagePayload {
    clientId: string;
    text: string;
    /** Defaults to `practice_to_parent`; parent accounts must pass `parent_to_practice`. */
    direction?: 'practice_to_parent' | 'parent_to_practice';
}

export function useSendPortalMessage() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: SendPortalMessagePayload) => {
            const res = await api.post<PortalMessage>('/portal-messages', payload);
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['portal-messages'] });
            qc.invalidateQueries({ queryKey: ['portal', 'overview'] });
        },
    });
}

export function useMarkPortalMessageRead() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, read }: { id: string; read: boolean }) => {
            const res = await api.patch<PortalMessage>(`/portal-messages/${id}`, { read });
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['portal-messages'] });
            qc.invalidateQueries({ queryKey: ['portal', 'overview'] });
        },
    });
}
